/**
 * Persona Wizard page (P1.T6).
 *
 * Spec ref: docs/ai-expert-rag-demo-skills-tools-workflows-SPEC.md §6.5
 * (three-mode model — wizard is mode 1), §6.6 (AI assistance).
 *
 * Two visible phases:
 *   1. "Answers" — 5-step form (business, customers, personality,
 *      languages, starter recipe) plus optional persona name.
 *      Step indicator + Back/Next, validation per step.
 *   2. "Preview" — after the wizard generates a persona + bundle,
 *      shows the LLM's explanation, the bundle's blocks, and a live-
 *      preview chat panel pinned right that calls /simulate.
 *
 * Discard / Publish at the bottom of the preview phase. Publish just
 * navigates to /personas (the persona is already persisted from the
 * wizard call); Discard calls DELETE.
 */

import { useMemo, useState } from 'react'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  MessageSquare,
  Sparkles,
} from 'lucide-react'
import { Button, Input, Textarea, Spinner } from '@/components/ui'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/utils'
import { usePersonaWizard } from '@/hooks/usePersonaWizard'
import {
  STARTER_RECIPES,
  type SimulateResponse,
  type WizardAnswers,
  type WizardResponse,
} from '@/types/personaWizard'

const TOTAL_STEPS = 5

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'it', label: 'Italian' },
]

const PERSONALITY_PRESETS = [
  'Warm and friendly',
  'Formal and precise',
  'Playful and energetic',
  'Calm and reassuring',
  'Direct and efficient',
]

// ── Step indicator ──────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2" aria-label="wizard-step-indicator">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((step) => (
        <div key={step} className="flex items-center gap-2">
          <div
            className={cn(
              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors',
              step < current
                ? 'bg-primary text-primary-foreground'
                : step === current
                  ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                  : 'bg-muted text-muted-foreground'
            )}
          >
            {step < current ? <Check className="w-3.5 h-3.5" /> : step}
          </div>
          {step < TOTAL_STEPS && (
            <div
              className={cn(
                'w-8 h-0.5 rounded-full transition-colors',
                step < current ? 'bg-primary' : 'bg-muted'
              )}
            />
          )}
        </div>
      ))}
    </div>
  )
}

// ── Validation per step ────────────────────────────────────────────────────

function isStepValid(step: number, answers: WizardAnswers): boolean {
  switch (step) {
    case 1:
      return answers.business_description.trim().length > 0
    case 2:
      return answers.customers.trim().length > 0
    case 3:
      return answers.personality.trim().length > 0
    case 4:
      return answers.languages.length > 0
    case 5:
      return true // starter_recipe is optional
    default:
      return false
  }
}

// ── Step panels ────────────────────────────────────────────────────────────

interface StepProps {
  answers: WizardAnswers
  onChange: (patch: Partial<WizardAnswers>) => void
}

function Step1Business({ answers, onChange }: StepProps) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold mb-1">What does your business do?</h2>
        <p className="text-sm text-muted-foreground">
          A few sentences. The wizard mines this for the persona's tone, vocabulary, and starter
          knowledge.
        </p>
      </div>
      <Textarea
        autoFocus
        rows={5}
        value={answers.business_description}
        onChange={(e) => onChange({ business_description: e.target.value })}
        placeholder="We run a small artisan bakery in Mexico City specializing in custom cakes…"
        aria-label="business-description"
      />
    </div>
  )
}

function Step2Customers({ answers, onChange }: StepProps) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold mb-1">Who are your customers?</h2>
        <p className="text-sm text-muted-foreground">
          Who are they and what do they typically ask for? Helps the persona anticipate intents.
        </p>
      </div>
      <Textarea
        autoFocus
        rows={5}
        value={answers.customers}
        onChange={(e) => onChange({ customers: e.target.value })}
        placeholder="Local foodies and event planners. They ask about flavors, allergens, lead time, and pricing."
        aria-label="customers"
      />
    </div>
  )
}

function Step3Personality({ answers, onChange }: StepProps) {
  const isCustom = !PERSONALITY_PRESETS.includes(answers.personality)
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold mb-1">What personality should the persona have?</h2>
        <p className="text-sm text-muted-foreground">
          Pick a preset or type your own.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {PERSONALITY_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => onChange({ personality: preset })}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm border transition-colors',
              answers.personality === preset
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted'
            )}
            aria-pressed={answers.personality === preset}
          >
            {preset}
          </button>
        ))}
      </div>
      <Input
        value={isCustom ? answers.personality : ''}
        onChange={(e) => onChange({ personality: e.target.value })}
        placeholder="Or describe a custom personality…"
        aria-label="personality-custom"
      />
    </div>
  )
}

function Step4Languages({ answers, onChange }: StepProps) {
  const toggle = (code: string) => {
    const next = answers.languages.includes(code)
      ? answers.languages.filter((l) => l !== code)
      : [...answers.languages, code]
    onChange({ languages: next.length ? next : ['en'] })
  }
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold mb-1">Which languages should the persona speak?</h2>
        <p className="text-sm text-muted-foreground">
          Choose at least one. The runtime's STT/TTS picks up these as auto-detect hints.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            type="button"
            onClick={() => toggle(lang.code)}
            className={cn(
              'px-3 py-1.5 rounded-full text-sm border transition-colors',
              answers.languages.includes(lang.code)
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted'
            )}
            aria-pressed={answers.languages.includes(lang.code)}
          >
            {lang.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function Step5Recipe({ answers, onChange }: StepProps) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold mb-1">Pick a starter recipe (optional)</h2>
        <p className="text-sm text-muted-foreground">
          Templates seed the persona with sensible default skills. You can change them later.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => onChange({ starter_recipe: null })}
          className={cn(
            'px-3 py-2 rounded-md text-sm border text-left transition-colors',
            answers.starter_recipe === null
              ? 'bg-primary text-primary-foreground border-primary'
              : 'border-border hover:bg-muted'
          )}
          aria-pressed={answers.starter_recipe === null}
        >
          <div className="font-medium">Blank slate</div>
          <div className="text-xs opacity-80">No starter skills</div>
        </button>
        {Object.entries(STARTER_RECIPES).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange({ starter_recipe: key })}
            className={cn(
              'px-3 py-2 rounded-md text-sm border text-left transition-colors',
              answers.starter_recipe === key
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:bg-muted'
            )}
            aria-pressed={answers.starter_recipe === key}
          >
            <div className="font-medium">{label}</div>
            <div className="text-xs opacity-80">{key}</div>
          </button>
        ))}
      </div>

      <div className="pt-2">
        <label className="block text-sm font-medium mb-1">
          Persona name <span className="text-muted-foreground">(optional)</span>
        </label>
        <Input
          value={answers.persona_name ?? ''}
          onChange={(e) => onChange({ persona_name: e.target.value || null })}
          placeholder="The wizard will suggest one if you leave this blank."
          aria-label="persona-name"
        />
      </div>
    </div>
  )
}

// ── Preview phase ──────────────────────────────────────────────────────────

interface PreviewProps {
  result: WizardResponse
  simulate: ReturnType<typeof usePersonaWizard>['simulate']
  discard: ReturnType<typeof usePersonaWizard>['discard']
}

function PreviewPanel({ result, simulate, discard }: PreviewProps) {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const [userMessage, setUserMessage] = useState('')
  const [lastSim, setLastSim] = useState<SimulateResponse | null>(null)

  const handleSimulate = async () => {
    if (!userMessage.trim()) return
    try {
      const res = await simulate.mutateAsync({
        bundleId: result.bundle.id,
        body: { user_message: userMessage },
      })
      setLastSim(res)
    } catch (err) {
      addToast({ title: 'Something went wrong', description: String(err), type: 'error' })
    }
  }

  const handlePublish = () => {
    addToast({ title: 'Persona published', type: 'success' })
    // Spec D5: land on the Workshop's Review tab (not auto-run — "Review
    // now" is the call to action). `rail` is consumed exactly once by the
    // Workshop, which strips it via replace-navigation.
    navigate(`/personas/${result.persona_id}/workshop?rail=review`)
  }

  const handleDiscard = async () => {
    try {
      await discard.mutateAsync(result.persona_id)
      addToast({ title: 'Discarded draft persona', type: 'info' })
      navigate('/personas')
    } catch (err) {
      addToast({ title: 'Something went wrong', description: String(err), type: 'error' })
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
      {/* Left: generated bundle */}
      <div className="space-y-4">
        <div className="rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-semibold">Generated persona</h3>
          </div>
          {result.explanation && (
            <p className="text-sm text-foreground mb-3">{result.explanation}</p>
          )}
          <ul className="space-y-3">
            {result.bundle.blocks.map((block, i) => (
              <li key={i} className="text-sm">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-0.5">
                  {block.category}
                </div>
                <div className="whitespace-pre-wrap">{block.content}</div>
              </li>
            ))}
          </ul>
          {result.bundle.default_skills.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                Default skills
              </div>
              <div className="flex flex-wrap gap-1.5">
                {result.bundle.default_skills.map((s, i) => (
                  <span
                    key={i}
                    className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary"
                  >
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: live preview chat */}
      <aside className="rounded-lg border border-border p-4 flex flex-col gap-3 lg:max-h-[70vh] lg:sticky lg:top-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <h3 className="font-semibold">Live preview</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Type a sample customer message. The composer renders the bundle into a system prompt and
          calls the LLM for a one-turn reply.
        </p>
        <Textarea
          rows={3}
          value={userMessage}
          onChange={(e) => setUserMessage(e.target.value)}
          placeholder="What flavors do you have?"
          aria-label="simulate-user-message"
        />
        <Button
          onClick={handleSimulate}
          disabled={simulate.isPending || !userMessage.trim()}
          leftIcon={
            simulate.isPending ? <Spinner size="sm" /> : <Sparkles className="w-4 h-4" />
          }
        >
          Simulate one turn
        </Button>
        {lastSim && (
          <div className="flex flex-col gap-2 text-sm">
            <div className="rounded-sm bg-muted p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">You</div>
              <div className="whitespace-pre-wrap">{userMessage}</div>
            </div>
            <div className="rounded-sm bg-primary/10 p-3">
              <div className="text-xs uppercase tracking-wider text-primary mb-1">
                Persona
              </div>
              <div className="whitespace-pre-wrap" aria-label="simulate-assistant-reply">
                {lastSim.assistant_reply}
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {lastSim.target_format} · {lastSim.token_estimate} tokens
            </div>
          </div>
        )}
      </aside>

      <div className="lg:col-span-2 flex items-center gap-3 pt-3 border-t border-border">
        <Button
          variant="secondary"
          onClick={handleDiscard}
          disabled={discard.isPending}
        >
          Discard
        </Button>
        <div className="flex-1" />
        <Button onClick={handlePublish}>Publish</Button>
      </div>
    </div>
  )
}

// ── Page shell ─────────────────────────────────────────────────────────────

export function PersonaWizard() {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { wizard, simulate, discard } = usePersonaWizard()
  const [step, setStep] = useState(1)
  const [result, setResult] = useState<WizardResponse | null>(null)
  const [answers, setAnswers] = useState<WizardAnswers>({
    business_description: '',
    customers: '',
    personality: '',
    languages: ['en'],
    starter_recipe: null,
    persona_name: null,
  })

  const canGoNext = useMemo(() => isStepValid(step, answers), [step, answers])

  const handleNext = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    if (step > 1) setStep(step - 1)
    else navigate('/personas')
  }

  const handleGenerate = async () => {
    try {
      const res = await wizard.mutateAsync(answers)
      setResult(res)
    } catch (err) {
      addToast({ title: 'Something went wrong', description: String(err), type: 'error' })
    }
  }

  return (
    <PageFrame width="detail" className="space-y-6" aria-label="persona-wizard">
      <PageHeader
        title="New persona — Wizard"
        subtitle="Answer five questions, then review and refine."
        actions={!result && <StepIndicator current={step} />}
      />

      {result ? (
        <PreviewPanel
          result={result}
          simulate={simulate}
          discard={discard}
        />
      ) : (
        <div className="space-y-6">
          <div className="rounded-lg border border-border p-5">
            {step === 1 && <Step1Business answers={answers} onChange={(p) => setAnswers({ ...answers, ...p })} />}
            {step === 2 && <Step2Customers answers={answers} onChange={(p) => setAnswers({ ...answers, ...p })} />}
            {step === 3 && <Step3Personality answers={answers} onChange={(p) => setAnswers({ ...answers, ...p })} />}
            {step === 4 && <Step4Languages answers={answers} onChange={(p) => setAnswers({ ...answers, ...p })} />}
            {step === 5 && <Step5Recipe answers={answers} onChange={(p) => setAnswers({ ...answers, ...p })} />}
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={handleBack}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </Button>
            <div className="flex-1" />
            {step < TOTAL_STEPS ? (
              <Button
                onClick={handleNext}
                disabled={!canGoNext}
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={!canGoNext || wizard.isPending}
                leftIcon={
                  wizard.isPending ? <Spinner size="sm" /> : <Sparkles className="w-4 h-4" />
                }
              >
                Generate persona
              </Button>
            )}
          </div>
        </div>
      )}
    </PageFrame>
  )
}

export default PersonaWizard
